import { Button } from "../../ui/buttons";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/Card";
import { useEffect, useState } from "react";
import { Label } from "@radix-ui/react-label";
import { useLocation } from "wouter";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  designationAPI,
  departmentAPI,
  employeeStatusAPI,
  employmentTypeAPI,
  workLocationAPI,
  nationalityAPI,
} from "./API";
import {
  createEmployeeAPI,
  getEmployeeManagerOptionsAPI,
  uploadEmployeeDocumentAPI,
} from "./employeeAPI";
import HRDocuments from "./NewEmployee/hrDocuments";
import PersonalDocuments from "./NewEmployee/personalDocuments";
import CompanyAssets from "./NewEmployee/companyAssets";
import Education from "./NewEmployee/education";
import WorkExperience from "./NewEmployee/workExperience";
import ReportingManager from "./NewEmployee/reportingManager";
import BasicInfo from "./NewEmployee/basicInfo";
import Designation from "./NewEmployee/designation";
import EmergencyContact from "./NewEmployee/emergencyContact";
// import Compensation from "./NewEmployee/compensation";
import BankDetail from "./NewEmployee/bankDetail";
import Documents from "./NewEmployee/Documents";
import Address from "./NewEmployee/address";
import { State } from "country-state-city";
import Employment from "./NewEmployee/employment";

export const SectionTitle = ({ children }) => (
  <Label className="text-base font-semibold">{children}</Label>
);

const emptyWorkExperience = {
  fromDate: null,
  toDate: null,
  companyName: "",
  jobLocation: "",
  designation: "",
};

const emptyEducation = {
  fromDate: null,
  toDate: null,
  instituteName: "",
  location: "",
  type: "",
  courseName: "",
};

const emptyAsset = {
  modelNumber: "",
  assetName: "",
  serialNumber: "",
  status: "",
  issuedOn: null,
  returnedOn: null,
};

const defaultPersonalDocuments = [
  { category: "PERSONAL", type: "Aadhar Front", fileUrl: "" },
  { category: "PERSONAL", type: "Aadhar Back", fileUrl: "" },
  { category: "PERSONAL", type: "Pan Card", fileUrl: "" },
  { category: "PERSONAL", type: "Driving License", fileUrl: "" },
  { category: "PERSONAL", type: "Passport", fileUrl: "" },
  { category: "PERSONAL", type: "Resume", fileUrl: "" },
];

const emptyHrDocument = {
  category: "HR",
  type: "",
  fileUrl: "",
};

export const mapNameOptions = (arr = []) =>
  arr.map((item) => ({
    label: item.name,
    value: item.name,
  }));

export const formatAadhar = (value = "") => {
  const digits = value.replace(/\D/g, "").slice(0, 12);
  return digits.replace(/(.{4})/g, "$1 ").trim();
};

const capitalizeWords = (value = "") =>
  value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

export const onlyDigits = (value = "", max = 999) =>
  value.replace(/\D/g, "").slice(0, max);

export const dateValue = (value) => {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
};

export default function EmployeeForm({ mode = "create" }) {
  const [, navigate] = useLocation();
  const dispatch = useDispatch();

  const [uploadingKey, setUploadingKey] = useState("");
  const [activeSection, setActiveSection] = useState(0);

  const {
    loading: isSaving,
    uploadLoading,
    updateLoading,
  } = useSelector((state) => state.employee);

  const [formData, setFormData] = useState({
    employeeId: "",
    profilePhoto: "",

    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    fatherName: "",
    motherName: "",
    maritalStatus: "",
    spouseName: "",
    personalEmail: "",
    personalMobile: "",
    officialEmail: "",
    officialMobile: "",
    gender: "",
    dob: null,
    nationality: "",
    bloodGroup: "",

    department: "",
    employeeStatus: "",
    employmentType: "",
    workLocation: "",
    companyName: "",
    dateOfJoining: null,
    dateOfConfirmation: null,
    probation: "",
    dateOfResignation: null,
    dateOfReleaving: null,
    noticePeriod: "",

    permanentAddress: "",
    permanentState: null,
    permanentCity: null,
    permanentPinCode: "",
    currentAddress: "",
    currentState: null,
    currentCity: null,
    currentPinCode: "",

    aadharNumber: "",
    panNumber: "",
    pfNumber: "",
    uanNumber: "",
    drivingLicenseNumber: "",
    passportNumber: "",

    bankName: "",
    bankAccountNumber: "",
    ifscCode: "",
    bankHolderName: "",
    swiftCode: "",

    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactEmail: "",
    emergencyContactAddress: "",
    relation: "",
  });

  const [designationHistory, setDesignationHistory] = useState([
    { fromDate: null, toDate: null, designation: "" },
  ]);

  const [managerHistory, setManagerHistory] = useState([
    { fromDate: null, toDate: null, reportingManager: "" },
  ]);

  const [workExperience, setWorkExperience] = useState([]);
  const [educationDetails, setEducationDetails] = useState([
    { ...emptyEducation },
  ]);
  const [assets, setAssets] = useState([]);

  const [personalDocuments, setPersonalDocuments] = useState(
    defaultPersonalDocuments,
  );

  const [hrDocuments, setHrDocuments] = useState([]);

  const sections = [
    "Basic Info",
    "Employment",
    "Address",
    "Documents",
    "Bank Details",
    // "Compensation",
    "Emergency Contact",
    "Designation",
    "Reporting Manager",
    "Work Experience",
    "Education",
    "Company Assets",
    "Personal Documents",
    "HR Documents",
  ];

  useEffect(() => {
    dispatch(designationAPI.fetch());
    dispatch(nationalityAPI.fetch());
    dispatch(departmentAPI.fetch());
    dispatch(employeeStatusAPI.fetch());
    dispatch(employmentTypeAPI.fetch());
    dispatch(workLocationAPI.fetch());
    dispatch(getEmployeeManagerOptionsAPI());
  }, [dispatch]);

  const handleOnChange = (key, value) => {
    const capitalizeFields = [
      "firstName",
      "middleName",
      "lastName",
      "fatherName",
      "motherName",
      "spouseName",
      "bankHolderName",
      "emergencyContactName",
    ];

    const upperFields = [
      "panNumber",
      "pfNumber",
      "uanNumber",
      "drivingLicenseNumber",
      "passportNumber",
      "ifscCode",
      "swiftCode",
    ];

    if (capitalizeFields.includes(key)) value = capitalizeWords(value);
    if (upperFields.includes(key)) value = value.toUpperCase();
    if (key === "aadharNumber") value = formatAadhar(value);
    if (
      ["personalMobile", "officialMobile", "emergencyContactNumber"].includes(
        key,
      )
    ) {
      value = onlyDigits(value, 10);
    }
    if (["permanentPinCode", "currentPinCode"].includes(key))
      value = onlyDigits(value, 6);

    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateArray = (setter, index, key, value) => {
    setter((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );
  };

  const removeArrayItem = (setter, index) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (file, callback, key) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Single file size cannot be more than 5MB");
      return;
    }

    try {
      setUploadingKey(key);
      const res = await dispatch(uploadEmployeeDocumentAPI(file)).unwrap();
      callback(res.url);
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error(error || "File upload failed");
    } finally {
      setUploadingKey("");
    }
  };

  const hasDateClash = (rows) => {
    const ranges = rows
      .filter((x) => x.fromDate)
      .map((x) => ({
        from: new Date(x.fromDate),
        to: x.toDate ? new Date(x.toDate) : new Date("9999-12-31"),
      }))
      .sort((a, b) => a.from - b.from);

    for (let i = 1; i < ranges.length; i++) {
      if (ranges[i].from <= ranges[i - 1].to) return true;
    }
    return false;
  };

  const validateSection = (showToast = true) => {
    const err = (msg) => {
      if (showToast) toast.error(msg);
      return false;
    };
    if (!formData.profilePhoto.trim()) return err("Profile Photo is required");
    if (!formData.employeeId.trim()) return err("Employee ID is required");
    if (!formData.title) return err("Title is required");
    if (!formData.firstName.trim()) return err("First name is required");
    if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        formData.personalEmail,
      )
    ) {
      return err("Valid personal email is required");
    }
    if (!/^[6-9]\d{9}$/.test(formData.personalMobile))
      return err("Valid personal mobile number is required");
    if (
      formData.officialEmail &&
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        formData.officialEmail,
      )
    ) {
      return err("Valid official email is required");
    }
    if (
      formData.officialMobile &&
      !/^[6-9]\d{9}$/.test(formData.officialMobile)
    ) {
      return err("Valid official mobile number is required");
    }
    if (!formData.gender) return err("Gender is required");
    if (!formData.dob) return err("Date of birth is required");
    if (!formData.nationality) return err("Nationality is required");
    if (!formData.bloodGroup) return err("Blood group is required");

    if (!formData.department) return err("Department is required");
    if (!formData.employeeStatus) return err("Employee status is required");
    if (!formData.employmentType) return err("Employment type is required");
    if (!formData.workLocation) return err("Work location is required");
    if (!formData.companyName) return err("Company Name is required");
    if (!formData.dateOfJoining) return err("Date of joining is required");
    if (!formData.dateOfConfirmation)
      return err("Date of confirmation is required");

    if (!formData.permanentAddress.trim())
      return err("Permanent address is required");
    if (!formData.permanentState) return err("Permanent state is required");
    if (!formData.permanentCity) return err("Permanent city is required");
    if (!/^[1-9][0-9]{5}$/.test(formData.permanentPinCode))
      return err("Valid permanent pin code is required");
    if (!formData.currentAddress.trim())
      return err("Current address is required");
    if (!formData.currentState) return err("Current state is required");
    if (!formData.currentCity) return err("Current city is required");
    if (!/^[1-9][0-9]{5}$/.test(formData.currentPinCode))
      return err("Valid current pin code is required");

    if (formData.aadharNumber.replace(/\D/g, "").length !== 12)
      return err("Aadhar number must be 12 digits");
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber))
      return err("Valid PAN number is required");

    if (!formData.bankAccountNumber.trim())
      return err("Bank account number is required");
    if (!formData.bankName.trim()) return err("Bank name is required");
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode))
      return err("Valid IFSC code is required");
    if (!formData.bankHolderName.trim())
      return err("Bank holder name is required");

    // if (!payrollDetails.length)
    //   return err("At least one compensation row is required");
    // for (const item of payrollDetails) {
    //   if (!item.fromDate) return err("Compensation from date is required");
    //   if (!item.basicSalary) return err("Basic salary is required");
    //   if (!item.hra) return err("HRA is required");
    // }
    // if (hasDateClash(payrollDetails))
    //   return err("Compensation dates should not clash");

    if (!formData.emergencyContactName.trim())
      return err("Emergency contact name is required");
    if (!/^[6-9]\d{9}$/.test(formData.emergencyContactNumber))
      return err("Valid emergency contact number is required");
    if (
      formData.emergencyContactEmail &&
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        formData.emergencyContactEmail,
      )
    ) {
      return err("Valid emergency contact email is required");
    }
    if (!formData.emergencyContactAddress.trim())
      return err("Emergency contact address is required");
    if (!formData.relation.trim()) return err("Relation is required");

    for (const item of designationHistory) {
      if (!item.fromDate) return err("Designation from date is required");
      if (!item.designation) return err("Designation is required");
    }
    if (hasDateClash(designationHistory))
      return err("Designation dates should not clash");

    for (const item of managerHistory) {
      if (!item.fromDate) return err("Reporting manager from date is required");
      if (!item.reportingManager) return err("Reporting manager is required");
    }
    if (hasDateClash(managerHistory))
      return err("Reporting manager dates should not clash");

    if (workExperience.length > 0) {
      for (const item of workExperience) {
        if (
          !item.fromDate ||
          !item.toDate ||
          !item.companyName ||
          !item.jobLocation ||
          !item.designation
        ) {
          return err("Fill all required work experience fields");
        }
      }
    }

    for (const item of educationDetails) {
      if (
        !item.fromDate ||
        !item.toDate ||
        !item.instituteName ||
        !item.location ||
        !item.type ||
        !item.courseName
      ) {
        return err("Fill all required education fields");
      }
    }

    // if (educationDetails.length < 3) {
    //   return err(
    //     "Please fill all education details including 10th/ 12th/ Graduation",
    //   );
    // }
    if (assets.length > 0) {
      for (const item of assets) {
        if (!item.assetName || !item.status || !item.issuedOn)
          return err("Fill all required asset fields");
      }
    }

    const required = ["Aadhar Front", "Aadhar Back", "Pan Card"];
    for (const type of required) {
      const doc = personalDocuments.find((x) => x.type === type);
      if (!doc?.fileUrl) return err(`${type} document is required`);
    }

    if (hrDocuments.length > 0) {
      for (const item of hrDocuments) {
        if (!item.type || !item.fileUrl)
          return err("HR document type and file are required");
      }
    }

    // if (!hrDocuments.some((item) => item.type === "Offer Letter")) {
    //   return err("Please upload Offer Letter in HR Documents");
    // }

    return true;
  };

  const validateAll = () => {
    if (!validateSection(true)) {
      return false;
    }
    return true;
  };

  const nextSection = (e) => {
    e?.preventDefault();

    setActiveSection((prev) => (prev < sections.length - 1 ? prev + 1 : prev));
  };

  const prevSection = () => {
    if (activeSection > 0) setActiveSection((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;

    const selectedPermanentState = State.getStatesOfCountry("IN").find(
      (s) => s.isoCode === formData.permanentState,
    );
    const selectedCurrentState = State.getStatesOfCountry("IN").find(
      (s) => s.isoCode === formData.currentState,
    );

    const finalPayload = {
      ...formData,
      permanentState: selectedPermanentState?.name || formData.permanentState,
      currentState: selectedCurrentState?.name || formData.currentState,
      aadharNumber: formData.aadharNumber.replace(/\D/g, ""),
      noticePeriod: formData.noticePeriod ? formData.noticePeriod : "",
      designationHistory,
      managerHistory,
      payrollDetails: null,
      workExperience,
      educationDetails,
      assets,
      documents: [...personalDocuments, ...hrDocuments].filter(
        (x) => x.fileUrl,
      ),
    };

    try {
      const res = await dispatch(createEmployeeAPI(finalPayload)).unwrap();

      toast.success(res?.message || "Employee created successfully");

      navigate("/employee-records");
    } catch (error) {
      toast.error(error || "Employee creation failed");
    }
  };

  const UploadLoader = ({ id }) =>
    uploadLoading && uploadingKey === id ? (
      <p className="text-xs text-blue-600 flex items-center gap-2">
        <i className="pi pi-spin pi-spinner" /> Uploading...
      </p>
    ) : null;

  return (
    <div>
      <div className="flex items-center mb-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Create New Employee
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Add a new Employee to your system
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employee Information</CardTitle>
          <CardDescription>
            Enter the details for the new employee
          </CardDescription>
        </CardHeader>

        <div className="inline-flex mx-6 mb-6 items-center justify-center rounded-md bg-accent p-1 text-muted-foreground">
          <div className="flex flex-wrap gap-2">
            {sections.map((section, index) => (
              <div
                key={index}
                type="button"
                onClick={() => setActiveSection(index)}
                className={`w-full md:w-fit px-4 py-2 rounded-md border text-sm font-medium transition-all ${
                  activeSection === index
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
              >
                {index + 1}. {section}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <CardContent className="flex flex-col gap-3">
            {activeSection === 0 && (
              <BasicInfo
                formData={formData}
                handleUpload={handleUpload}
                handleOnChange={handleOnChange}
                UploadLoader={UploadLoader}
              />
            )}

            {activeSection === 1 && (
              <Employment
                formData={formData}
                setFormData={setFormData}
                handleOnChange={handleOnChange}
              />
            )}

            {activeSection === 2 && (
              <Address
                formData={formData}
                handleOnChange={handleOnChange}
                setFormData={setFormData}
              />
            )}

            {activeSection === 3 && (
              <Documents formData={formData} handleOnChange={handleOnChange} />
            )}

            {activeSection === 4 && (
              <BankDetail formData={formData} handleOnChange={handleOnChange} />
            )}

            {/* {activeSection === 5 && (
              <Compensation
                payrollDetails={payrollDetails}
                setPayrollDetails={setPayrollDetails}
                emptyPayroll={emptyPayroll}
                calculateGrossAndNet={calculateGrossAndNet}
                updateArray={updateArray}
                removeArrayItem={removeArrayItem}
              />
            )} */}

            {activeSection === 5 && (
              <EmergencyContact
                formData={formData}
                handleOnChange={handleOnChange}
              />
            )}

            {activeSection === 6 && (
              <Designation
                designationHistory={designationHistory}
                updateArray={updateArray}
                setDesignationHistory={setDesignationHistory}
                removeArrayItem={removeArrayItem}
              />
            )}

            {activeSection === 7 && (
              <ReportingManager
                setManagerHistory={setManagerHistory}
                managerHistory={managerHistory}
                updateArray={updateArray}
                removeArrayItem={removeArrayItem}
              />
            )}

            {activeSection === 8 && (
              <WorkExperience
                workExperience={workExperience}
                setWorkExperience={setWorkExperience}
                emptyWorkExperience={emptyWorkExperience}
                updateArray={updateArray}
                removeArrayItem={removeArrayItem}
              />
            )}

            {activeSection === 9 && (
              <Education
                educationDetails={educationDetails}
                setEducationDetails={setEducationDetails}
                updateArray={updateArray}
                removeArrayItem={removeArrayItem}
                emptyEducation={emptyEducation}
              />
            )}

            {activeSection === 10 && (
              <CompanyAssets
                assets={assets}
                setAssets={setAssets}
                updateArray={updateArray}
                removeArrayItem={removeArrayItem}
                emptyAsset={emptyAsset}
              />
            )}

            {activeSection === 11 && (
              <PersonalDocuments
                personalDocuments={personalDocuments}
                setPersonalDocuments={setPersonalDocuments}
                UploadLoader={UploadLoader}
                handleUpload={handleUpload}
              />
            )}

            {activeSection === 12 && (
              <HRDocuments
                hrDocuments={hrDocuments}
                setHrDocuments={setHrDocuments}
                UploadLoader={UploadLoader}
                updateArray={updateArray}
                handleUpload={handleUpload}
                removeArrayItem={removeArrayItem}
                emptyHrDocument={emptyHrDocument}
              />
            )}
          </CardContent>

          <CardFooter className="flex justify-between items-center border-t pt-6">
            <div>
              {activeSection > 0 && (
                <Button type="button" variant="outline" onClick={prevSection}>
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-3">

              {activeSection < sections.length - 1 ? (
                <Button type="button" onClick={(e) => nextSection(e)}>
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSaving || updateLoading || uploadLoading}
                  className="min-w-[180px]"
                >
                  {isSaving || updateLoading ? (
                    <span className="flex items-center gap-2">
                      <i className="pi pi-spin pi-spinner" />
                      Saving...
                    </span>
                  ) : (
                    "Create Employee"
                  )}
                </Button>
              )}
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
